import { useState, useEffect } from 'react';
import { ERR_INDIVIDUAL_TIER_SOLO_ONLY } from '../lib/tier';

interface TierUpgradeDetail {
  error?:       string;
  currentTier?: string;
  requiredTier?: string;
  upgradeUrl?:  string;
}

/**
 * Error codes that are handled by a page-specific richer UI (upgrade modal),
 * so the generic banner should stay silent to avoid doubling up.
 */
const PAGE_HANDLED_CODES = new Set<string>([ERR_INDIVIDUAL_TIER_SOLO_ONLY]);

/**
 * Listens for the global `tier-upgrade-required` event dispatched by apiFetch
 * and shows an amber banner with upgrade call-to-action.
 *
 * Mount this component once near the root of the app (e.g. in App.tsx or a
 * layout component) so it can catch events from any page.
 */
export function TierUpgradeBanner() {
  const [detail, setDetail] = useState<TierUpgradeDetail | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const d = (e as CustomEvent<TierUpgradeDetail>).detail;
      // Skip error codes that have a richer page-specific UI.
      if (d?.error && PAGE_HANDLED_CODES.has(d.error)) return;
      setDetail(d);
    };
    window.addEventListener('tier-upgrade-required', handler);
    return () => window.removeEventListener('tier-upgrade-required', handler);
  }, []);

  if (!detail) return null;

  return (
    <div
      role="alert"
      style={{
        position:     'fixed',
        top:          116,
        left:         '50%',
        transform:    'translateX(-50%)',
        zIndex:       9999,
        minWidth:     320,
        maxWidth:     560,
        padding:      '14px 18px',
        background:   'rgba(245, 158, 11, 0.12)',
        border:       '1px solid rgba(245, 158, 11, 0.45)',
        borderRadius: 'var(--radius-sm, 6px)',
        display:      'flex',
        alignItems:   'flex-start',
        gap:          12,
      }}
    >
      <span style={{ fontSize: 16, lineHeight: 1, marginTop: 2 }}>⚠</span>
      <div style={{ flex: 1 }}>
        <p
          style={{
            fontFamily: 'var(--font-body, sans-serif)',
            fontSize:   13,
            fontWeight: 600,
            color:      'var(--warning, #f59e0b)',
            margin:     0,
            marginBottom: detail.currentTier ? 4 : 0,
          }}
        >
          {detail.error ?? 'Plan upgrade required'}
        </p>
        {detail.currentTier && detail.requiredTier && (
          <p
            style={{
              fontFamily: 'var(--font-body, sans-serif)',
              fontSize:   12,
              color:      'var(--text-secondary, #888)',
              margin:     0,
            }}
          >
            Your plan: <strong>{detail.currentTier}</strong> → Required:{' '}
            <strong>{detail.requiredTier}</strong>
          </p>
        )}
      </div>
      {detail.upgradeUrl && (
        <a
          href={detail.upgradeUrl}
          style={{
            fontFamily:     'var(--font-body, sans-serif)',
            fontSize:       12,
            fontWeight:     600,
            color:          'var(--warning, #f59e0b)',
            textDecoration: 'none',
            border:         '1px solid rgba(245, 158, 11, 0.45)',
            borderRadius:   'var(--radius-sm, 6px)',
            padding:        '4px 10px',
            whiteSpace:     'nowrap',
          }}
        >
          Upgrade
        </a>
      )}
      <button
        onClick={() => setDetail(null)}
        aria-label="Dismiss"
        style={{
          background:  'none',
          border:      'none',
          cursor:      'pointer',
          color:       'var(--text-secondary, #888)',
          fontSize:    16,
          lineHeight:  1,
          padding:     0,
          marginTop:   2,
        }}
      >
        ✕
      </button>
    </div>
  );
}
